import { useTranslation } from 'react-i18next';
import { Flex, HStack, IconButton, Text, Box } from '@chakra-ui/react';
import { HiChevronDown } from 'react-icons/hi2';
import {
  HiChevronDoubleLeft,
  HiChevronDoubleRight,
  HiChevronLeft,
  HiChevronRight,
} from 'react-icons/hi2';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalItems?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
}

/**
 * @useFor Standardized table pagination component with page controls, size selector, and item counts.
 */
export function Pagination({
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [8, 20, 50, 100],
}: PaginationProps) {
  const { t } = useTranslation();

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <Flex justify="space-between" align="center" mt={4} pt={4} borderTopWidth="1px" borderColor="border.primary">
      {/* Left: Page info */}
      <Text fontSize="sm" color="text.secondary">
        {t('common.paginationPage', 'Page {{current}} of {{total}}', { current: currentPage, total: totalPages })}
        {totalItems !== undefined && ` (${t('common.paginationItems', '{{count}} items', { count: totalItems })})`}
      </Text>

      {/* Center: Page navigation */}
      <HStack gap={1}>
        <IconButton
          aria-label={t('common.firstPage', 'First page')}
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          data-e2e="pagination-first-page"
        >
          <HiChevronDoubleLeft size={16} />
        </IconButton>

        <IconButton
          aria-label={t('common.previousPage', 'Previous page')}
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          data-e2e="pagination-prev-page"
        >
          <HiChevronLeft size={16} />
        </IconButton>

        {getPageNumbers().map((page, index) => {
          if (page === '...') {
            return (
              <Text key={`ellipsis-${index}`} px={2} fontSize="sm" color="text.secondary">
                ...
              </Text>
            );
          }

          const pageNum = page as number;
          const isActive = pageNum === currentPage;

          return (
            <IconButton
              key={pageNum}
              aria-label={t('common.pageNumber', 'Page {{number}}', { number: pageNum })}
              variant={isActive ? 'solid' : 'ghost'}
              size="sm"
              bg={isActive ? 'button.primary' : 'transparent'}
              color={isActive ? 'white' : 'text.primary'}
              onClick={() => onPageChange(pageNum)}
              fontWeight={isActive ? 'semibold' : 'normal'}
              data-e2e={`pagination-page-${pageNum}`}
              _hover={{
                bg: isActive ? 'button.primary' : 'surface.hover',
              }}
            >
              {pageNum}
            </IconButton>
          );
        })}

        <IconButton
          aria-label={t('common.nextPage', 'Next page')}
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          data-e2e="pagination-next-page"
        >
          <HiChevronRight size={16} />
        </IconButton>

        <IconButton
          aria-label={t('common.lastPage', 'Last page')}
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          data-e2e="pagination-last-page"
        >
          <HiChevronDoubleRight size={16} />
        </IconButton>
      </HStack>

      {/* Right: Page size selector */}
      {onPageSizeChange && (
        <HStack gap={2}>
          <Box position="relative">
            <select
              aria-label={t('common.rowsPerPage', 'Rows per page')}
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              style={{
                backgroundColor: 'surface.primary !important',
                borderRadius: '8px',
                padding: '6px 32px 6px 12px',
                fontSize: '14px',
                color: 'text.primary',
                appearance: 'none',
                cursor: 'pointer',
              }}
              data-e2e="pagination-size-selector"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size} / {t('common.page', 'page')}
                </option>
              ))}
            </select>
            <Box
              position="absolute"
              right={2}
              top="50%"
              transform="translateY(-50%)"
              pointerEvents="none"
              color="text.primary"
            >
              <HiChevronDown size={16} />
            </Box>
          </Box>
        </HStack>
      )}
    </Flex>
  );
}
